import { type ChangeEvent, type FC, type KeyboardEvent, useRef, useState } from 'react';
import { Flex, type InputRef } from 'antd';
import styled from 'styled-components';
import { TRANSLATED_ACTIONS } from '@/common/messages';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { useIntl } from '@/components/formatted_message/FormattedMessage';
import { Tag } from '@/components/tag/Tag';
import { TagType } from '@/components/tag/enums/TagType';
import { Typography } from '@/components/typography';
import { TypographyType } from '@/components/typography/enums/type';
import type { DataCyProp } from '@/types';
import { InputReactWrapper } from '../input';
import { CUSTOM_EMAILS_MESSAGES } from './messages';
import { isEmailValid } from '@/utils/isEmailValid';

const { AssistiveMessage } = Typography;

const StyledInput = styled(InputReactWrapper)`
  width: 280px;
`;

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export interface CustomEmailsProps extends DataCyProp {
  value?: string[];
  onChange?: (emails: string[]) => void;
  placeholder?: string;
}

export const CustomEmails: FC<CustomEmailsProps> = ({ value = [], onChange, placeholder, 'data-cy': dataCy }) => {
  const intl = useIntl();
  const inputRef = useRef<InputRef>(null);
  const [draft, setDraft] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);

  const email = normalizeEmail(draft);
  const isValid = isEmailValid(email);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraft(event.target.value);
    setIsDuplicate(false);
  };

  const handleAdd = () => {
    if (!isValid) return;

    if (value.some(existing => normalizeEmail(existing) === email)) {
      setIsDuplicate(true);
    } else {
      onChange?.([...value, email]);
      setDraft('');
    }

    inputRef.current?.focus();
  };

  const handlePressEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter must add the address, not submit the surrounding form.
    event.preventDefault();
    handleAdd();
  };

  const handleRemove = (removed: string) => onChange?.(value.filter(existing => existing !== removed));

  return (
    <Flex vertical data-cy={dataCy} gap={8}>
      <Flex vertical gap={4}>
        <Flex gap={8}>
          <StyledInput
            data-cy={dataCy && `${dataCy}-input`}
            placeholder={placeholder}
            ref={inputRef}
            status={isDuplicate ? 'error' : undefined}
            value={draft}
            onChange={handleChange}
            onPressEnter={handlePressEnter}
          />
          <ButtonAnt data-cy={dataCy && `${dataCy}-add`} disabled={!isValid} type="primary" onClick={handleAdd}>
            {intl.formatMessage(TRANSLATED_ACTIONS.add)}
          </ButtonAnt>
        </Flex>
        {isDuplicate && (
          <AssistiveMessage type={TypographyType.Error}>
            {intl.formatMessage(CUSTOM_EMAILS_MESSAGES.duplicate)}
          </AssistiveMessage>
        )}
      </Flex>
      {value.length > 0 && (
        <Flex wrap gap={8}>
          {value.map(existing => (
            <Tag closable key={existing} type={TagType.Normal} onClose={() => handleRemove(existing)}>
              {existing}
            </Tag>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
